import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { TranslationTabs } from './TranslationTabs';
import { BaseTextsTranslation } from './translations/BaseTextsTranslation';
import { QuickActionsTranslation } from './translations/QuickActionsTranslation';
import { MiniAppsTranslation } from './translations/MiniAppsTranslation';

interface TranslationModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: {
    code: string;
    label: string;
    flag: string;
  };
  translations?: {
    texts?: {
      title: string;
      subtitle: string;
      welcomeMessage: string;
      inputPlaceholder: string;
      errorMessage?: string;
    };
    quickActions?: string[];
    orderTracking?: {
      quickActionText: string;
      promptMessage: string;
      errorMessage: string;
    };
    miniApps?: {
      id: string;
      label: string;
      content?: {
        title?: string;
        description?: string;
        buttonText?: string;
        catalogTitle?: string;
        catalogSubtitle?: string;
        searchPlaceholder?: string;
        filterLabel?: string;
        sortLabel?: string;
        priceLabel?: string;
        stockLabel?: string;
        categoryLabel?: string;
        tagsLabel?: string;
        surfaceLabel?: string;
        roomsLabel?: string;
        locationLabel?: string;
        featuresLabel?: string;
        availableLabel?: string;
        soldLabel?: string;
        rentedLabel?: string;
        contactButtonLabel?: string;
        quickActionText?: string;
        promptMessage?: string;
        errorMessage?: string;
        productDetails?: {
          [id: string]: {
            name: string;
            description: string;
          };
        };
        serviceDetails?: {
          [id: string]: {
            title: string;
            subtitle: string;
            content: string;
            buttonText: string;
          };
        };
        propertyDetails?: {
          [id: string]: {
            title: string;
            description: string;
            features: string[];
          };
        };
      };
    }[];
  };
  onSave: (translations: any) => void;
}

export function TranslationModal({ isOpen, onClose, language, translations, onSave }: TranslationModalProps) {
  const { colors } = useDesignColors();
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'texts' | 'quickActions' | 'miniApps'>('texts');
  const [formData, setFormData] = useState({
    texts: {
      title: translations?.texts?.title || '',
      subtitle: translations?.texts?.subtitle || '',
      welcomeMessage: translations?.texts?.welcomeMessage || '',
      inputPlaceholder: translations?.texts?.inputPlaceholder || '',
      errorMessage: translations?.texts?.errorMessage || ''
    },
    quickActions: translations?.quickActions || [],
    orderTracking: {
      quickActionText: translations?.orderTracking?.quickActionText || '',
      promptMessage: translations?.orderTracking?.promptMessage || '',
      errorMessage: translations?.orderTracking?.errorMessage || ''
    },
    miniApps: translations?.miniApps || []
  });

  // Handle click outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Vérifier si Order Tracking existe dans les mini-apps et le transférer
    const orderTrackingMiniApp = formData.miniApps.find(app => app.id === 'order-tracking');
    
    // Si des données d'order tracking existent dans les mini-apps
    if (orderTrackingMiniApp && orderTrackingMiniApp.content) {
      // Mettre à jour formData.orderTracking avec les données trouvées
      formData.orderTracking = {
        quickActionText: orderTrackingMiniApp.content.quickActionText || formData.orderTracking.quickActionText,
        promptMessage: orderTrackingMiniApp.content.promptMessage || formData.orderTracking.promptMessage,
        errorMessage: orderTrackingMiniApp.content.errorMessage || formData.orderTracking.errorMessage
      };
    }
    
    onSave(formData);
    onClose();
  };

  const updateFormData = (section: keyof typeof formData, data: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div 
        ref={modalRef}
        className="w-full max-w-6xl bg-gray-900/95 rounded-xl border border-white/10 flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{language.flag}</span>
            <h3 className="text-xl font-semibold text-white">
              Translations in {language.label}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs - Now in a container with overflow handling */}
        <div className="border-b border-white/10 overflow-x-auto">
          <div className="min-w-max">
            <TranslationTabs activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <form id="translationForm" onSubmit={handleSubmit} className="space-y-8 w-full max-w-full">
            {activeTab === 'texts' && (
              <BaseTextsTranslation
                language={language}
                formData={formData.texts}
                onChange={(data) => updateFormData('texts', data)}
              />
            )}
            {activeTab === 'quickActions' && (
              <QuickActionsTranslation
                language={language}
                formData={formData.quickActions}
                onChange={(data) => updateFormData('quickActions', data)}
              />
            )}
            {activeTab === 'miniApps' && (
              <MiniAppsTranslation
                language={language}
                formData={formData.miniApps}
                onChange={(data) => updateFormData('miniApps', data)}
                orderTrackingData={formData.orderTracking}
              />
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-white/10 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-lg text-white transition-all"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
            }}
          >
            Save translations
          </button>
        </div>
      </div>
    </div>
  );
}